import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';


// Track if org fetch is in progress globally to prevent duplicate requests
let isFetchingOrg = false;

export function useAuth() {
  const navigate = useNavigate();
  const { user, session, isLoading, isInitialized } = useAuthStore();
  const initRef = useRef(false);

  // Fetch user's organization after login
  const fetchUserOrganization = useCallback(async (userId: string) => {
    const { setLoading, setOrganization, currentOrg } = useOrgStore.getState();

    // Skip if already fetching or already have org data
    if (isFetchingOrg || currentOrg) {
      return;
    }

    isFetchingOrg = true;
    setLoading(true);
    try {
      // Get auth token from localStorage
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );

      if (!authKey) {
        console.error('No auth token found');
        isFetchingOrg = false;
        setLoading(false);
        return;
      }

      const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
      const token = authData?.access_token;

      if (!token) {
        console.error('No access token found');
        isFetchingOrg = false;
        setLoading(false);
        return;
      }

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Get user's organization membership using raw fetch
      const membershipRes = await fetch(
        `${baseUrl}/rest/v1/organization_members?user_id=eq.${userId}&status=eq.active`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const memberships = await membershipRes.json();

      if (!memberships || memberships.length === 0) {
        console.warn('No organization found for user');
        isFetchingOrg = false;
        setLoading(false);
        return;
      }

      // Take the first active membership
      const membership = memberships[0];

      // Get organization details
      const orgRes = await fetch(
        `${baseUrl}/rest/v1/organizations?id=eq.${membership.organization_id}`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const orgs = await orgRes.json();

      if (orgs && orgs.length > 0) {
        setOrganization(orgs[0], membership);
        isFetchingOrg = false;
      } else {
        console.error('Organization not found');
        isFetchingOrg = false;
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      isFetchingOrg = false;
      setLoading(false);
    }
  }, []); // No dependencies - uses getState() internally

  // Initialize auth state on mount
  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current) {
      return;
    }
    initRef.current = true;

    let mounted = true;

    async function initAuth() {
      const { setAuth } = useAuthStore.getState();
      try {
        // Get session directly from localStorage to avoid hanging getSession() call
        const supabaseAuthKey = Object.keys(localStorage).find(key =>
          key.startsWith('sb-') && key.endsWith('-auth-token')
        );

        let currentSession = null;
        if (supabaseAuthKey) {
          const authData = JSON.parse(localStorage.getItem(supabaseAuthKey) || '{}');
          if (authData?.access_token && authData?.user) {
            currentSession = authData;
          }
        }

        if (mounted) {
          if (currentSession?.user) {
            setAuth(currentSession.user, currentSession);
            await fetchUserOrganization(currentSession.user.id);
          } else {
            setAuth(null, null);
            // No user, so no organization to load
            useOrgStore.getState().setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setAuth(null, null);
          useOrgStore.getState().setLoading(false);
        }
      }
    }

    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (mounted) {
        const { setAuth, clearAuth } = useAuthStore.getState();
        if (event === 'SIGNED_IN' && currentSession?.user) {
          setAuth(currentSession.user, currentSession);
          await fetchUserOrganization(currentSession.user.id);
        } else if (event === 'SIGNED_OUT') {
          clearAuth();
          useOrgStore.getState().clearOrganization();
          isFetchingOrg = false; // Reset on logout
        } else if (event === 'TOKEN_REFRESHED' && currentSession) {
          setAuth(currentSession.user, currentSession);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - fetchUserOrganization is stable

  // Login with email and password
  const login = useCallback(
    async (email: string, password: string) => {
      const { setAuth, setLoading } = useAuthStore.getState();
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          setAuth(data.user, data.session);

          // Fetch organization in background - don't block login success
          fetchUserOrganization(data.user.id).catch((err) => {
            console.error('Failed to fetch organization after login:', err);
          });

          return { success: true, user: data.user };
        }

        return { success: false, error: 'No user returned' };
      } catch (error: any) {
        console.error('Login error:', error);
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [fetchUserOrganization]
  );

  // Register new user
  const register = useCallback(
    async (email: string, password: string, fullName?: string, organizationName?: string) => {
      const { setLoading } = useAuthStore.getState();
      setLoading(true);
      try {
        // Create auth user with metadata
        // The database trigger (handle_new_user) will automatically create:
        // 1. User profile
        // 2. Organization
        // 3. Organization membership (admin role)
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              organization_name: organizationName || `${fullName || email.split('@')[0]}'s Organization`,
            },
          },
        });

        if (error) throw error;
        if (!data.user) throw new Error('No user returned from signup');

        // Wait briefly for the database trigger to complete
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Fetch the organization created by the trigger
        if (data.session) {
          await fetchUserOrganization(data.user.id);
        }

        return { success: true, user: data.user };
      } catch (error: any) {
        console.error('Registration error:', error);
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [fetchUserOrganization]
  );

  // Logout
  const logout = useCallback(async () => {
    const { setLoading, clearAuth } = useAuthStore.getState();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      clearAuth();
      useOrgStore.getState().clearOrganization();
      navigate('/login');
      return { success: true };
    } catch (error: any) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Request password reset
  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Password reset request error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Update password
  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Password update error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Check if email is verified (Supabase sets email_confirmed_at when verified)
  const isEmailVerified = !!user?.email_confirmed_at;

  // Resend verification email
  const resendVerificationEmail = useCallback(async () => {
    if (!user?.email) return { success: false, error: 'No email found' };

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Resend verification error:', error);
      return { success: false, error: error.message };
    }
  }, [user?.email]);

  return {
    user,
    session,
    isLoading,
    isInitialized,
    isAuthenticated: !!user,
    isEmailVerified,
    login,
    register,
    logout,
    requestPasswordReset,
    updatePassword,
    resendVerificationEmail,
  };
}
